<template>
  <div class="ui-alert" :class="[`ui-alert--${tone}`]" role="status">
    <hs-icon :name="resolvedIcon" :tone="tone" soft :size="18" box-size="2.6rem" />
    <div class="ui-alert__content">
      <strong v-if="title">{{ title }}</strong>
      <p v-if="$slots.default || message">
        <slot>{{ message }}</slot>
      </p>
    </div>
    <button v-if="dismissible" type="button" class="ui-alert__close" @click="$emit('dismiss')">
      <hs-icon name="x" :size="15" />
    </button>
  </div>
</template>

<script>
import HsIcon from "./HsIcon.vue";

export default {
  name: "HsAlert",
  components: { HsIcon },
  props: {
    tone: {
      type: String,
      default: "info",
      validator: (value) => ["info", "success", "warning", "danger", "neutral", "primary"].includes(value),
    },
    title: {
      type: String,
      default: "",
    },
    message: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    dismissible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["dismiss"],
  computed: {
    resolvedIcon() {
      if (this.icon) return this.icon;
      const icons = {
        success: "check-circle",
        warning: "alert-triangle",
        danger: "alert-circle",
        neutral: "info",
        primary: "sparkles",
        info: "info",
      };
      return icons[this.tone] || "info";
    },
  },
};
</script>

<style scoped>
.ui-alert {
  align-items: flex-start;
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 92%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 20px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
  display: flex;
  gap: 0.85rem;
  padding: 0.9rem;
}

.ui-alert--success {
  border-color: color-mix(in srgb, #22c55e 28%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.ui-alert--warning {
  border-color: color-mix(in srgb, #f59e0b 32%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.ui-alert--danger {
  border-color: color-mix(in srgb, #ef4444 28%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.ui-alert__content {
  flex: 1;
  min-width: 0;
}

.ui-alert strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.92rem;
  font-weight: 950;
  line-height: 1.25;
}

.ui-alert p {
  color: var(--hs-muted, #64748b);
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.55;
  margin: 0.2rem 0 0;
}

.ui-alert__close {
  align-items: center;
  border-radius: 12px;
  color: var(--hs-muted, #64748b);
  display: inline-flex;
  height: 2rem;
  justify-content: center;
  width: 2rem;
}

.ui-alert__close:hover {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
}
</style>
